/**
 * useItemReservations - Hook for managing item reservation data
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useReservations } from '../../reservations.shared/context/ReservationsContext';

export const useItemReservations = () => {
  const {
    reservations,
    loading,
    error,
    filters,
    pagination,
    setLoading,
    setError,
    setReservations,
    addReservation,
    updateReservation,
    removeReservation,
    setFilters,
    setPagination
  } = useReservations();

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter reservations to only show item reservations (those with inventoryItemId but not serviceId)
  const itemReservations = reservations.filter(reservation => 
    reservation.inventoryItemId && !reservation.serviceId
  );

  // Fetch item reservations from API
  const fetchReservations = useCallback(async (customFilters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      
      // Apply filters
      const activeFilters = { ...filters, ...customFilters };
      
      if (activeFilters.status && activeFilters.status !== 'all') {
        params.append('status', activeFilters.status);
      }
      
      if (activeFilters.dateRange?.startDate) {
        params.append('startDate', activeFilters.dateRange.startDate);
      }
      
      if (activeFilters.dateRange?.endDate) {
        params.append('endDate', activeFilters.dateRange.endDate);
      }
      
      if (activeFilters.searchTerm) {
        params.append('search', activeFilters.searchTerm);
      }

      // Add pagination
      params.append('page', pagination.currentPage.toString());
      params.append('limit', pagination.itemsPerPage.toString());

      // Use the items-specific endpoint which already filters on the backend
      const response = await axios.get(`/api/reservations/items?${params}`);

      if (response.data?.success) {
        // The API returns { success: true, data: [...], pagination: {...} }
        const itemReservations = response.data.data || [];

        setReservations(itemReservations);

        // Update pagination info from backend (now accurate for item reservations only)
        setPagination({
          ...pagination,
          totalItems: response.data.pagination?.total || itemReservations.length
        });

        return itemReservations;
      } else {
        throw new Error(response.data?.message || 'Failed to fetch reservations');
      }
    } catch (err) {
      console.error('Failed to fetch item reservations:', err);
      setError(err.message || 'Failed to load reservations');
      return [];
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage, setLoading, setError, setReservations, setPagination]);

  // Create new item reservation
  const createReservation = useCallback(async (reservationData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post('/api/reservations/integration/all', reservationData);
      
      if (response.data?.success) {
        const newReservation = response.data.data;
        addReservation(newReservation);
        return newReservation;
      } else {
        throw new Error(response.data?.message || 'Failed to create reservation');
      }
    } catch (err) {
      console.error('Failed to create item reservation:', err);
      setError(err.message || 'Failed to create reservation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, addReservation]);

  // Process item return
  const processReturn = useCallback(async (reservationId, returnData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`/api/reservations/processing/${reservationId}/return`, returnData);
      
      if (response.data?.success) {
        const updatedReservation = response.data.data;
        updateReservation(updatedReservation);
        return updatedReservation;
      } else {
        throw new Error(response.data?.message || 'Failed to process return');
      }
    } catch (err) {
      console.error('Failed to process return:', err);
      setError(err.message || 'Failed to process return');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, updateReservation]);

  // Cancel reservation
  const cancelReservation = useCallback(async (reservationId, cancelData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`/api/reservations/processing/${reservationId}/cancel`, cancelData);
      
      if (response.data?.success) {
        const updatedReservation = response.data.data;
        updateReservation(updatedReservation);
        return updatedReservation;
      } else {
        throw new Error(response.data?.message || 'Failed to cancel reservation');
      }
    } catch (err) {
      console.error('Failed to cancel reservation:', err);
      setError(err.message || 'Failed to cancel reservation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, updateReservation]);

  // Reprocess reservation
  const reprocessReservation = useCallback(async (reservationId, reprocessData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`/api/reservations/processing/${reservationId}/reprocess`, reprocessData);
      
      if (response.data?.success) {
        const updatedReservation = response.data.data;
        updateReservation(updatedReservation);
        return updatedReservation;
      } else {
        throw new Error(response.data?.message || 'Failed to reprocess reservation');
      }
    } catch (err) {
      console.error('Failed to reprocess reservation:', err);
      setError(err.message || 'Failed to reprocess reservation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, updateReservation]);

  // Delete reservation
  const deleteReservation = useCallback(async (reservationId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.delete(`/api/reservations/processing/${reservationId}`);
      
      if (response.data?.success) {
        removeReservation(reservationId);
        return true;
      } else {
        throw new Error(response.data?.message || 'Failed to delete reservation');
      }
    } catch (err) {
      console.error('Failed to delete reservation:', err);
      setError(err.message || 'Failed to delete reservation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, removeReservation]);

  // Apply filters
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination({ ...pagination, currentPage: 1 }); // Reset to first page
  }, [setFilters, setPagination, pagination]);

  // Sort reservations
  const sortReservations = useCallback((key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    
    const sorted = [...itemReservations].sort((a, b) => {
      if (!a[key] || !b[key]) return 0;
      
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle nested properties
      if (key.includes('.')) {
        const keys = key.split('.');
        aVal = keys.reduce((obj, k) => obj?.[k], a);
        bVal = keys.reduce((obj, k) => obj?.[k], b);
      }
      
      // Handle dates
      if (key.includes('Time') || key.includes('Date')) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setReservations(sorted);
  }, [itemReservations, sortConfig, setReservations]);

  // Auto-fetch on mount and filter changes
  useEffect(() => {
    fetchReservations();
  }, [filters, pagination.currentPage]);

  return {
    // Data
    reservations: itemReservations,
    loading,
    error,
    filters,
    pagination,
    sortConfig,
    
    // Actions
    fetchReservations,
    createReservation,
    processReturn,
    cancelReservation,
    reprocessReservation,
    deleteReservation,
    applyFilters,
    sortReservations,
    
    // Pagination
    setPagination
  };
};

export default useItemReservations;